import { computed, effect, inject, signal, untracked } from '@angular/core'

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'

import { CommentOwnerService } from '@flowx/designer/modules/ai-builder/services/comment-owner.service'
import { CommentSessionService } from '@flowx/designer/modules/ai-builder/services/comment-session.service'
import { HarnessConfigService } from '@flowx/designer/modules/ai-builder/services/harness-config.service'
import { AiBuilderStore } from '@flowx/designer/modules/ai-builder/store/ai-builder.store'
import { HarnessSessionStore } from '@flowx/designer/modules/ai-builder/store/harness-session.store'
import type {
  HarnessTurnAdmission,
  HarnessTurnOptions,
} from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import {
  type CommentRun,
  commentRefusal,
  toCommentUiContext,
} from '@flowx/designer/modules/ai-builder/utils/comment-mode/comment-ui-context.util'

type CommentModeState = {
  /** The page on screen can be commented on (a UI flow run page or a process run page). */
  pageOffersCommentMode: boolean
  /** The maker pressed Comment and has not left the mode. */
  commentModeOn: boolean
  /** The run on screen started from the head of its branch: the work in progress, or the latest
   * commit. Unknown counts as not at the head. */
  runAtBranchHead: boolean
  /** The branch the run's version is on; empty while unknown. */
  runBranchName: string
  /** A comment is on its way to the harness: the box waits for the answer. */
  sending: boolean
  /** Why the last attempt to send did not go, for the box to say; null when there is nothing to say. */
  sendError: string | null
}

const initialState: CommentModeState = {
  pageOffersCommentMode: false,
  commentModeOn: false,
  runAtBranchHead: false,
  runBranchName: '',
  sending: false,
  sendError: null,
}

/** Said in the box while the open conversation is executing a plan and a comment would need a new one. */
const NEW_CONVERSATION_BLOCKED = 'Available when the assistant finishes the current run'

const NOT_THE_PAGES_VERSION = 'This run is not on the version open on the page'

const unsentMessage = (admission: Exclude<HarnessTurnAdmission, { kind: 'admitted' }>): string =>
  admission.kind === 'blocked' ? admission.reason : admission.message

/**
 * Comment mode on a running UI flow or process: the maker picks a component of the running UI and
 * writes what should change. The page says it offers the mode and which version its run uses; the
 * assistant decides whether it is on offer now, and the mode can only be entered on the head of the
 * branch. Provided by the layout, next to the bar that carries the toggle and the page that reads the
 * mode, so both see the same one.
 */
export const CommentModeStore = signalStore(
  withState(initialState),
  // The page's run is read when a comment is sent, not when the page offers the mode: a UI flow's
  // session id is only known once the run has started. A function is not state to render from.
  withProps(() => ({ _pageRun: signal<(() => CommentRun) | null>(null) })),
  withComputed(
    (store, aiBuilder = inject(AiBuilderStore), session = inject(HarnessSessionStore)) => {
      /** The toggle is on the bar: the page offers it and the assistant is open. */
      const offered = computed(() => store.pageOffersCommentMode() && aiBuilder.assistOn())
      /** The open conversation takes the comment: it is not still opening, the run is on a branch
       * of its own (a run on `main` always gets a new one), and the conversation writes to that
       * branch or to none yet. */
      const inOpenConversation = computed(() => {
        const branch = store.runBranchName()
        const writeTarget = session.writeTarget()

        return (
          !!session.sessionId() &&
          !session.openingSessionId() &&
          !!branch &&
          branch !== 'main' &&
          (!writeTarget || writeTarget.branch_name === branch)
        )
      })
      /** The comment needs a new conversation and the open one is executing a plan. */
      const newConversationBlocked = computed(
        () => !inOpenConversation() && session.newConversationBlocked()
      )

      return {
        offered,
        atBranchHead: store.runAtBranchHead,
        branchName: store.runBranchName,
        /** A turn in flight holds the page, so the mode cannot be entered meanwhile; leaving it
         * stays possible. */
        turnOnBlockedByStreaming: session.isStreaming,
        /** A comment cannot be sent now: a reply is in flight, a conversation is opening, the
         * comment is on its way, or a new conversation is not possible yet. */
        sendBlocked: computed(
          () =>
            session.isStreaming() ||
            !!session.openingSessionId() ||
            store.sending() ||
            newConversationBlocked()
        ),
        /** The line the box says about sending: what went wrong, else why it cannot go yet. */
        sendMessage: computed(
          () => store.sendError() ?? (newConversationBlocked() ? NEW_CONVERSATION_BLOCKED : null)
        ),
        inOpenConversation,
        /** The mode can be entered now. */
        canTurnOn: computed(() => offered() && store.runAtBranchHead() && !session.isStreaming()),
        /** The page is to pick components now. */
        on: computed(() => store.commentModeOn() && offered() && store.runAtBranchHead()),
      }
    }
  ),
  withMethods((store) => {
    const config = inject(HarnessConfigService)
    const session = inject(HarnessSessionStore)
    const commentSession = inject(CommentSessionService)
    const commentOwner = inject(CommentOwnerService)
    const aiBuilder = inject(AiBuilderStore)

    const fail = (sendError: string): false => {
      patchState(store, { sendError })

      return false
    }

    /** Sends the turn to the open conversation, or to a new one when it is not the run's. Blocked
     * when the open one cannot be left yet. */
    const sendComment = (
      text: string,
      options: HarnessTurnOptions
    ): Promise<HarnessTurnAdmission> => {
      if (!store.inOpenConversation() && !session.newSession()) {
        return Promise.resolve({ kind: 'blocked', reason: NEW_CONVERSATION_BLOCKED })
      }

      return session.isAnsweringInterrupt()
        ? session.respond(text, options)
        : session.sendTurn(text, options)
    }

    return {
      /** The page on screen offers comment mode, until it withdraws. `run` says which run a
       * comment is on; it is called when the comment is sent. Returns the page's withdraw, which
       * does nothing once another page has offered since. */
      offerFromPage: (run: () => CommentRun): (() => void) => {
        store._pageRun.set(run)
        patchState(store, { pageOffersCommentMode: true })

        return () => {
          if (store._pageRun() !== run) {
            return
          }

          store._pageRun.set(null)
          patchState(store, {
            pageOffersCommentMode: false,
            runAtBranchHead: false,
            runBranchName: '',
          })
        }
      },

      /** The version the run on screen uses, as far as the head of its branch goes. */
      reportRunVersion: (version: { atBranchHead: boolean; branchName: string }): void =>
        patchState(store, {
          runAtBranchHead: version.atBranchHead,
          runBranchName: version.branchName,
        }),

      toggle: (): void => {
        if (store.on()) {
          patchState(store, { commentModeOn: false })
        } else if (store.canTurnOn()) {
          patchState(store, { commentModeOn: true })
        }
      },

      /** The box has changed what a send would say: its text or its target. */
      clearSendError: (): void => patchState(store, { sendError: null }),

      /**
       * Sends the comment on the component the box is on, as a turn of the run's conversation. The
       * box stays, with its text, when the target is no longer on screen and when the comment does
       * not go (`sendMessage` says why); it closes, and this resolves true, once the harness has
       * admitted the turn. The rail then shows the conversation.
       */
      send: async (prompt: string): Promise<boolean> => {
        const run = store._pageRun()
        const picked = commentSession.targetIfOnScreen()

        if (!run || !picked || store.sending()) {
          return false
        }

        const text = prompt.trim()
        const resource = run()
        const refusal = commentRefusal(picked, resource)

        if (!text) {
          return fail('The comment is empty')
        }

        if (refusal) {
          return fail(`This comment cannot be sent: ${refusal}`)
        }

        // The run's version comes from the page that ran it, the turn's from the route: a page
        // that is not on the run's version would send a turn that reads another version.
        if (resource.appVersionId !== config.routeContext()?.app_version_id) {
          return fail(NOT_THE_PAGES_VERSION)
        }

        const box = commentSession.commentBox()
        const component = box?.crumbs[box.selectedIndex]?.displayName ?? picked.target.label

        // Held before the owner is looked for, which can take a while: a second Enter sends nothing.
        patchState(store, { sending: true, sendError: null })

        const { owner, ownerMissing } = await commentOwner.ownerOf(picked.target, resource)
        const built = toCommentUiContext(picked, resource, owner)
        const admission: HarnessTurnAdmission =
          'refused' in built
            ? { kind: 'failed', message: `This comment cannot be sent: ${built.refused}` }
            : await sendComment(text, {
                uiContext: built.uiContext,
                comment: {
                  component,
                  page: resource.resourceName,
                  contextRead: true,
                  ...(ownerMissing && { ownerMissing: true as const }),
                },
              })

        patchState(store, { sending: false })

        if (admission.kind !== 'admitted') {
          return fail(unsentMessage(admission))
        }

        commentSession.close()
        aiBuilder.closeList()
        aiBuilder.backToMake()

        return true
      },
    }
  }),
  withHooks((store) => {
    const aiBuilder = inject(AiBuilderStore)
    const commentSession = inject(CommentSessionService)
    // Moving to another level or component, or closing the box, ends what the last send said.
    const boxKey = computed(() => {
      const box = commentSession.commentBox()

      return box && `${box.draftNumber}|${box.selectedIndex}`
    })

    return {
      onInit: (): void => {
        // Leaving the offer, or the head of the branch, ends the mode for good: coming back to it
        // starts from off, not from wherever the maker left it.
        effect(() => {
          if (!store.offered() || !store.atBranchHead()) {
            patchState(store, { commentModeOn: false })
          }
        })

        // A document landing while the maker comments does not take the page away.
        effect(() => aiBuilder.holdMakeView(store.on()))

        effect(() => {
          boxKey()
          untracked(() => store.clearSendError())
        })
      },
      onDestroy: (): void => aiBuilder.holdMakeView(false),
    }
  })
)
